import React from 'react';
import { Grid, InputAdornment, styled, TextField, Tooltip, Typography } from '@mui/material';
import MessageIcon from '@mui/icons-material/Message';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import RemarkDialogBox from '../../shared/remark-dialog-box';
import { TDaysOfWeeks } from '../../types/time-entry.type';

interface ITimeEntryFieldsInputProps {
  time: TDaysOfWeeks;
  onTimeChange: (updatedTime: any) => void;
  onIsActiveChange: (isActive: boolean) => void;
}

const TimeEntryFields: React.FC<ITimeEntryFieldsInputProps> = ({
  time,
  onTimeChange,
  onIsActiveChange
}) => {
  const [description, setDescription] = React.useState<string>(null);
  const [remarkData, setRemarkData] = React.useState<any>(null);
  const [timeEntryValue, setTimeEntryValue] = React.useState<string>('0h 0m');
  const [finalizeTimeEntryValue, setFinalizeTimeEntryValue] = React.useState<string>('0h 0m');
  const [isTimeEntryError, setIsTimeEntryError] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  const handleTimeEntryInput = (event) => {
    let value = event.target.value;
    setTimeEntryValue(value);
    setIsTimeEntryError(false);
  }

  const convertToHoursAndMinutes = (totalMinutes: number) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours > 0 ? hours + 'h ' : '0h '}${minutes > 0 ? minutes + 'm' : '0m'}`.trim();
  };

  const submitTimeEntryValue = () => {
    const regex = /^(\d+h\s\d+m|\d+m|\d+h|\d+)$/;
    let value = timeEntryValue.trim();

    if (!regex.test(value)) {
      setIsTimeEntryError(true);
      setErrorMessage('Value must be in the format 4h 35m.');
      return;
    }

    let totalMinutes = 0;
    if (/^\d+$/.test(value)) {
      totalMinutes = parseInt(value, 10);
      value = convertToHoursAndMinutes(totalMinutes);
    } else {
      const hoursMatch = value.match(/(\d+)h/);
      const minutesMatch = value.match(/(\d+)m/);

      if (hoursMatch) {
        totalMinutes += parseInt(hoursMatch[1], 10) * 60;
      }
      if (minutesMatch) {
        totalMinutes += parseInt(minutesMatch[1], 10);
      }

      value = convertToHoursAndMinutes(totalMinutes);
    }

    // Validate total minutes
    if (totalMinutes > 1440) {
      setIsTimeEntryError(true);
      setErrorMessage('Maximum allowed time is 1440 minutes (24 hours).');
      return;
    } else {
      setIsTimeEntryError(false);
      setErrorMessage('');
    }

    setTimeEntryValue(value);

    //set finalize time entry value
    if (!isTimeEntryError) {
      setFinalizeTimeEntryValue(value);
      onIsActiveChange(false);
    }
  }

  const cancelTimeEntryValue = () => {
    setTimeEntryValue(finalizeTimeEntryValue);
    setIsTimeEntryError(false);
    onIsActiveChange(false);
  }

  const addDescription = () => {
    setRemarkData({
      type: 'REMARK',
      title: 'Description',
      submitBtnName: description?.length > 0 ? 'Update' : 'Save',
      open: true
    });
  };

  const handleSaveDescription = (data: string) => {
    setDescription(data);
    setRemarkData(null);
  };

  //set initial value
  React.useEffect(() => {
    const timeValue = time?.hoursWorked ? time.hoursWorked : '0h 0m';
    setTimeEntryValue(timeValue);
    setFinalizeTimeEntryValue(timeValue);
    setDescription(time?.description);
  }, [time]);

  React.useEffect(() => {
    const updatedTime = {
      id: time?.id,
      entryDate: time.entryDate,
      day: time.day,
      hoursWorked: finalizeTimeEntryValue,
      description
    };
    onTimeChange(updatedTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalizeTimeEntryValue, description]);

  return (
    <Grid>
      <Grid className="time-entry-main-content" sx={{ position: 'relative' }}>
        <Grid className="time-field-content">
          <Grid>
            <TextField
              autoFocus
              inputProps={{ className: 'time-entry-text-field' }}
              onChange={handleTimeEntryInput}
              value={timeEntryValue}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isTimeEntryError && (
                      <Tooltip
                        title={
                          <Typography
                            style={{
                              fontSize: '11px',
                              wordBreak: 'break-word'
                            }}
                          >
                            {errorMessage}
                          </Typography>
                        }
                        arrow
                      >
                        <WarningIcon
                          sx={{
                            color: '#e67e22',
                            fontSize: '1.2rem',
                            marginRight: '-10px'
                          }}
                        />
                      </Tooltip>
                    )}
                  </InputAdornment>
                )
              }}
              error={isTimeEntryError}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  submitTimeEntryValue();
                }
              }}
              onFocus={(event) => {
                event.target.select();
              }}
            />
          </Grid>
        </Grid>
        <Grid
          sx={{
            position: 'absolute',
            top: '28px',
            right: '45px',
            display: 'flex',
            gap: '10px',
            zIndex: 1000
          }}
        >
          <CheckIcon
            sx={{
              fontSize: '1.3rem',
              cursor: 'pointer',
              borderRadius: '4px',
              padding: '2px',
              color: 'green',
            }}
            onClick={submitTimeEntryValue}
          />
          <CloseIcon
            sx={{
              fontSize: '1.3rem',
              cursor: 'pointer',
              borderRadius: '4px',
              padding: '2px',
              color: 'red',
            }}
            onClick={cancelTimeEntryValue}
          />
        </Grid>
        <Grid sx={{ display: 'flex', flexDirection: 'column' }}>
          <Tooltip
            title={
              <Typography
                style={{
                  fontSize: '11px'
                }}
              >
                Add Description
              </Typography>
            }
            arrow
          >
            <MessageIcon
              onClick={addDescription}
              sx={{
                cursor: 'pointer',
                fontSize: '20px',
                marginLeft: '16px'
              }}
            />
          </Tooltip>
        </Grid>
      </Grid>
      <RemarkDialogBox
        submitBtnName={remarkData?.submitBtnName}
        title={remarkData?.title}
        open={remarkData?.open}
        getRemark={description}
        handleClose={() => setRemarkData(null)}
        onSubmit={handleSaveDescription}
      />
    </Grid>
  );
};

export default TimeEntryFields;
